import React, { ReactNode } from "react";

import {
  RadioProps,
  useRadio,
  Stack,
  Box,
  Flex,
  Text,
  Center,
} from "@chakra-ui/react";

type RadioCardProps = RadioProps & { tag?: ReactNode, index: number };

const RadioCard = React.forwardRef<HTMLInputElement, RadioCardProps>(
  ({ tag, index, ...props }, ref) => {
    const { getInputProps, getCheckboxProps, getLabelProps } = useRadio(props);

    const input = getInputProps(undefined, ref);
    const checkbox = getCheckboxProps();
    const label = getLabelProps();

    const isChecked = checkbox?.["data-checked"] !== undefined;
    const isHovered = checkbox?.["data-hover"] !== undefined;
    const isFocus = checkbox?.["data-focus"] !== undefined;
    const isInvalid = checkbox?.["data-invalid"] !== undefined;

    const isHoveredOrFocus = isHovered || isFocus;

    return (
      <Box as="label" maxWidth="2xl" id={`${props?.name}.${index}`}>
        <input {...input} required={false} />
        <Flex
          {...checkbox}
          justify="space-between"
          ring="1px"
          rounded="lg"
          w="full"
          ringColor="gray.200"
          px="5"
          py="4"
          ml={0}
          alignItems="center"
          cursor="pointer"
          color="darkBrand"
          bgColor="white"
          _disabled={{
            cursor: "not-allowed",
            pointerEvents: "none",
          }}
          _checked={{
            bgColor: "brand",
            color: "white",
            ringColor: "brand",
            ring: "2px",
          }}
          _focus={{
            ringColor: "brand",
          }}
          _hover={{
            ringColor: "brand",
          }}
          _invalid={{
            color: "invalid.500",
            ringColor: "invalid.500",
            bgColor: "white",
            _hover: {
              color: "brand",
              ringColor: "brand",
            },
            _focus: {
              color: "brand",
              ringColor: "brand",
            },
          }}
        >
          <Stack spacing="3" direction="row" align="center">
            <Center
              position="relative"
              flexShrink={0}
              border="2px"
              borderColor={
                isChecked || isHoveredOrFocus
                  ? "brand"
                  : isInvalid
                  ? "invalid.500"
                  : "gray.200"
              }
              bgColor={
                isHoveredOrFocus ? "white" : isInvalid ? "invalid.500" : "white"
              }
              rounded="full"
              justify="center"
              alignItems="center"
              width="6"
              height="6"
            >
              <Box
                width="4"
                height="4"
                rounded="full"
                border="4px"
                borderColor="white"
                bgColor={isChecked ? "brand" : "white"}
              />
            </Center>
            <Text
              color={
                isChecked
                  ? "white"
                  : isHoveredOrFocus
                  ? "brand"
                  : isInvalid
                  ? "invalid.500"
                  : "gray.500"
              }
              fontWeight={isChecked ? "medium" : "base"}
              {...label}
            >
              {props.children}
            </Text>
          </Stack>
          {tag}
        </Flex>
      </Box>
    );
  }
);

export default RadioCard;
